import { Component, DestroyRef, effect, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TourUiStateService } from '../../services/tour/tour-ui-state.service';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { CommonModule } from '@angular/common';
import { catchError, filter, interval, of, startWith, switchMap } from 'rxjs';
import { AuthenticationFacade } from '../../../views/authentication/facade/authentication.facade';
import { NavigationFacade } from '../../state/navigation/facades/navigation-facade';
import { IMenuItem } from '../../models/menu-items';
import { NavigationService } from '../../services/navigation.service';
import { VersionService } from '../../services/version.service';
import { NotificationService } from '../../../views/notifications/services/notification.service';
import packageJson from '../../../../../package.json';
import { UserMgmtFacade } from '../../../views/user-management/facade/user-mgmt.facade';
import { LabFacade } from '../../../views/labs/facade/lab.facade';

// Same cadence as the notification bell and the system alert banner.
const POLL_INTERVAL_MS = 5 * 60 * 1000;

@Component({
  selector: 'app-side-bottom-menu',
  templateUrl: './side-bottom-menu.component.html',
  styleUrls: ['./side-bottom-menu.component.css'],
  imports: [RouterModule, CommonModule],
})
export class SideBottomMenuComponent implements OnInit {
  private authfacade = inject(AuthenticationFacade);
  private navFacade = inject(NavigationFacade);
  private navService = inject(NavigationService);
  private router = inject(Router);
  private versionService = inject(VersionService);
  private notificationService = inject(NotificationService);
  private destroyRef = inject(DestroyRef);
  private userMgmtFacade = inject(UserMgmtFacade);
  private labFacade = inject(LabFacade);
  userSignal = this.userMgmtFacade.getCurrentUserSignal;
  bottomMenuItems: IMenuItem[] = [];
  currentRoute = '';
  isMenuOpen = false;
  readonly appVersion = packageJson.version;
  backendVersion: string | null = null;
  private activeAlertCount = 0;

  // Icon mapping for menu items - using SVG icons
  readonly menuIconMap: { [key: string]: string } = {
    // 'Your Account': '/icons/user.svg',
    Notifications: '/icons/bi-notifications.svg',
    'System Alerts': '/icons/icons _ bi-alarm-exclamation.svg',
    'Acceptable Use Policy': '/icons/icon _ doc.svg',
    // 'Activity Logs': '/icons/icons _ bi-alarm-exclamation.svg',
    Support: '/icons/question-mark-circle.svg',
    'User Guide': '/icons/icon _ doc.svg',
    'Report Issues': '/icons/warning-triangle-outline.svg',
    'Django Admin': '/icons/hi-outline-cog8tooth.svg',
    // 'Log Out': '/icons/question-mark-circle.svg',
  };

  getMenuIcon(menuName: string): string | null {
    return this.menuIconMap[menuName] || null;
  }

  /** Notifications opens filtered to the lab selected at the top of the side nav. */
  queryParamsFor(menuItem: IMenuItem): { lab: number } | null {
    const selectedLab = this.labFacade.selectedLabSignal();
    return menuItem.state === 'notifications' && selectedLab ? { lab: selectedLab.id } : null;
  }

  isSelected(menuItem: IMenuItem): boolean {
    if (!menuItem.state) return false;
    return this.currentRoute.startsWith(`/${menuItem.state}`);
  }
  private tourUi = inject(TourUiStateService);
  private currentUserGuideUrl = '';

  constructor() {
    effect(() => {
      if (this.tourUi.bottomPanelForcedOpen() && !this.isMenuOpen) {
        this.isMenuOpen = true;
      }
    });

    // rebuild bottom menu when the authenticated user resolves so role-gated
    // items appear once /me has returned, even if the
    // version endpoint emitted first.
    effect(() => {
      this.navService.currentUser();
      this.rebuildMenu();
    });
  }

  selectNavItem(selectedItem: { name: string }) {
    this.navFacade.setSideNavMenuItems([{ name: selectedItem.name }]);
  }

  ngOnInit(): void {
    this.currentRoute = this.router.url;

    // Update route on navigation
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.currentRoute = this.router.url;
      });

    // Same cached /api/version/ stream as backendVersion$ — applies user_guide_url to the menu.
    this.versionService.userGuideUrl$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((url) => {
        this.currentUserGuideUrl = url;
        this.rebuildMenu();
      });

    // Badges the System Alerts item with how many alerts are live right now.
    // The banner's endpoint hides acknowledged alerts, so it would undercount.
    // Alerts appear and expire while a session is open, so this polls rather
    // than reading once. catchError is inside the switchMap so a failed poll
    // leaves the badge alone without tearing the interval down.
    interval(POLL_INTERVAL_MS)
      .pipe(
        startWith(0),
        switchMap(() => this.notificationService.getAllSystemAlerts().pipe(catchError(() => of(null)))),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((alerts) => {
        if (alerts === null) {
          return;
        }
        this.activeAlertCount = alerts.filter((alert) => alert.is_currently_active).length;
        this.rebuildMenu();
      });

    // Subscribe to the cached singleton observable — only one HTTP request is
    // ever made for the entire app lifetime regardless of component recreation.
    this.versionService.backendVersion$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (ver) => {
          this.backendVersion = ver;
        },
        error: () => {
          // Leave as null if the version endpoint is unreachable
        },
      });
  }

  private rebuildMenu(): void {
    const items = this.applyUserGuideUrl(this.navService.getSideBottomMenuItems(), this.currentUserGuideUrl);
    this.bottomMenuItems = this.applyActiveAlertCount(items);
  }

  /** Badge shows only when there is at least one live alert. */
  private applyActiveAlertCount(items: IMenuItem[]): IMenuItem[] {
    return items.map((item) =>
      item.name === 'System Alerts' ? { ...item, badgeCount: this.activeAlertCount } : item,
    );
  }

  /** Sets User Guide external URL from the API; hides the item when URL is empty. */
  private applyUserGuideUrl(items: IMenuItem[], userGuideUrl: string): IMenuItem[] {
    const trimmed = userGuideUrl.trim();
    return items
      .map((item) =>
        item.name === 'User Guide' ? { ...item, externalUrl: trimmed || undefined } : item,
      )
      .filter((item) => !(item.name === 'User Guide' && !item.externalUrl));
  }

  toggleMenu(): void {
    this.isMenuOpen = !this.isMenuOpen;
  }

  logOut() {
    this.navFacade.setSideNavMenuItems([]);
    this.authfacade.postLogout();
    // localStorage.clear() and navigation are handled by the logout success effect
  }
}
